import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Crown, Music, Zap, Check, Star, Loader2 } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { toast } from "sonner";
import BeatPlayer from "../components/BeatPlayer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionPage = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [featuredBeats, setFeaturedBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, beatsRes] = await Promise.all([
          axios.get(`${API}/subscriptions/plans`),
          axios.get(`${API}/subscriptions/featured-beats`)
        ]);
        setPlans(plansRes.data.plans || []);
        setFeaturedBeats(beatsRes.data || []);

        if (user) {
          const subRes = await axios.get(`${API}/subscriptions/my`, { headers: getAuthHeaders(), withCredentials: true });
          setSubscription(subRes.data);
        }
      } catch (e) {} finally { setLoading(false); }
    };
    fetchData();
  }, [user]);

  const handleSubscribe = async (planId) => {
    if (!user) { toast.error("Please sign in first"); return; }
    setSubscribing(true);
    try {
      const res = await axios.post(`${API}/subscriptions/subscribe`, { plan: planId }, { headers: getAuthHeaders(), withCredentials: true });
      setSubscription({ ...res.data, subscribed: true });
      toast.success("Welcome to the Beat Club!");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to subscribe"); }
    finally { setSubscribing(false); }
  };

  const handleCancel = async () => {
    try {
      await axios.post(`${API}/subscriptions/cancel`, {}, { headers: getAuthHeaders(), withCredentials: true });
      setSubscription({ subscribed: false });
      toast.success("Subscription cancelled");
    } catch (e) { toast.error("Failed to cancel"); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-neon-cyan" />
    </div>
  );

  return (
    <div className="min-h-screen py-10 px-6" data-testid="subscription-page">
      <div className="max-w-5xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-14">
          <Badge className="bg-neon-yellow/20 text-neon-yellow border-neon-yellow/50 mb-4">
            <Crown className="w-3 h-3 mr-1" />Exclusive
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight mb-4">
            The Beat Club
          </h1>
          <p className="text-zinc-400 max-w-xl mx-auto text-sm md:text-base">
            Get exclusive access to curated beats every month. Download, create, and stay ahead of the game.
          </p>
        </div>

        {/* Active Sub */}
        {subscription?.subscribed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass border border-neon-yellow/30 p-8 mb-12 text-center"
            data-testid="active-subscription"
          >
            <Crown className="w-10 h-10 text-neon-yellow mx-auto mb-3" />
            <h2 className="font-heading text-2xl uppercase mb-2">You're a Member!</h2>
            <p className="text-zinc-400 text-sm mb-4">
              Plan: <span className="text-neon-yellow">{subscription.plan}</span> |
              Beats remaining: <span className="text-neon-green">{subscription.beats_remaining}</span> |
              Renews: {new Date(subscription.next_renewal).toLocaleDateString()}
            </p>
            <Button variant="outline" onClick={handleCancel} className="border-red-500/50 text-red-400 hover:bg-red-500/10" data-testid="cancel-subscription">
              Cancel Subscription
            </Button>
          </motion.div>
        )}

        {/* Plans */}
        {!subscription?.subscribed && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.15 }}
                className={`glass p-8 border relative overflow-hidden ${plan.id === 'yearly' ? 'border-neon-yellow/50' : 'border-white/10'}`}
                data-testid={`plan-${plan.id}`}
              >
                {plan.id === 'yearly' && (
                  <Badge className="absolute top-4 right-4 bg-neon-yellow text-black font-bold text-xs">Best Value</Badge>
                )}
                <div className="mb-6">
                  <h3 className="font-heading text-2xl uppercase">{plan.label}</h3>
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="text-4xl font-heading text-neon-cyan">${plan.price}</span>
                    <span className="text-zinc-500">/{plan.id === 'monthly' ? 'mo' : 'yr'}</span>
                  </div>
                  {plan.id === 'yearly' && (
                    <p className="text-xs text-neon-green mt-1">Save $30 vs monthly</p>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {[
                    `${plan.beats_per_month} exclusive beats/month`,
                    "Early access to new releases",
                    "Members-only flash sales",
                    "Priority support",
                    plan.id === 'yearly' ? "Free merch item annually" : null
                  ].filter(Boolean).map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                      <Check className="w-4 h-4 text-neon-green flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  className={`w-full font-bold uppercase ${plan.id === 'yearly' ? 'bg-neon-yellow text-black' : 'bg-neon-cyan text-black'}`}
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={subscribing}
                  data-testid={`subscribe-${plan.id}`}
                >
                  {subscribing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                  Subscribe Now
                </Button>
              </motion.div>
            ))}
          </div>
        )}

        {/* This Month's Featured Beats */}
        {featuredBeats.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-6">
              <Star className="w-5 h-5 text-neon-yellow" />
              <h2 className="font-heading text-xl uppercase">This Month's Featured Beats</h2>
            </div>
            <div className="space-y-3">
              {featuredBeats.map((beat) => (
                <BeatPlayer key={beat.beat_id} beat={beat} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;
